import { languageCodeSchemaForJSA } from '@shared/internationalization';
import { z } from 'zod';

import { paginationSchema } from '../../../shared/source/data-contract/pagination';
import { jobProfileSkillSchema, skillSchema } from '../../../shared/source/data-contract/skill';
import { relocationPreferenceSchema } from '../../../shared/source/data-contract/user';
import { jobProfileReviewerCandidateSchema } from '../job-architecture/job-profile';

import type { SetRequired } from 'type-fest';

const jobProfileStateSchema = z.enum(['draft', 'active', 'archived']);

export const jobProfileStructuredDescriptionSchema = z.object({
    tag: z.string().regex(/^[a-z]+[a-z_]*$/, 'Tag must be lowercase and can only contain letters and underscores'),
    value: z.string().max(100_000),
});

export const jobProfileTranslationsSchema = z.object({
    description: z.string().max(100_000).optional(),
    language: languageCodeSchemaForJSA,
    structured: jobProfileStructuredDescriptionSchema.array().optional(),
    title: z.string().max(256).min(3),
});

const jobProfileDescriptionGenerationResponseSchema = jobProfileTranslationsSchema.pick({
    language: true,
    structured: true,
});

export type JobProfileDescriptionGenerationResponseContent = z.infer<
    typeof jobProfileDescriptionGenerationResponseSchema
>;

export const jobProfileDescriptionGenerationResponseArraySchema = jobProfileDescriptionGenerationResponseSchema.array();

export const jobArchitectureProfileReviewStateSchema = z.union([z.literal('assigned'), z.literal('approved')]);

export type JobArchitectureProfileReviewState = z.infer<typeof jobArchitectureProfileReviewStateSchema>;

export const jobArchitectureProfileReviewerSchema = jobProfileReviewerCandidateSchema.extend({
    state: jobArchitectureProfileReviewStateSchema,
});

export type JobArchitectureProfileReviewer = z.infer<typeof jobArchitectureProfileReviewerSchema>;

export const creatorSchema = z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
});

export type Person = z.infer<typeof creatorSchema>;

export const jobProfileUpdatedBySchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
});

export const competencyTranslationSchema = z.object({
    language: z.string().max(5).min(5),
    title: z.string(),
});

const competencySchema = z.object({
    id: z.uuid(),
    title: z.string(),
    titles: competencyTranslationSchema.array(),
});

export type Competency = z.infer<typeof competencySchema>;

export const competenciesSchema = competencySchema.array().transform((competencies) => {
    return competencies.reduce((competencyMap, item) => {
        competencyMap.set(item.id, item);

        return competencyMap;
    }, new Map<string, Competency>());
});

export const getCompetencyTitle = (competency: Competency, languageCode?: string): string => {
    return competency.titles?.find((title) => title.language === languageCode)?.title ?? competency.title;
};

export type Competencies = z.infer<typeof competenciesSchema>;

export const jobProfileComponentSchema = z.object({
    id: z.uuid(),
    competencies: competenciesSchema.optional(),
    externalId: z.string().optional(),
    language: z.union([z.literal('de-DE'), z.literal('en-US')]).optional(),
    skills: jobProfileSkillSchema.array().optional(),
    structured: jobProfileStructuredDescriptionSchema.array().optional(),
    title: z.string(),
    translations: jobProfileTranslationsSchema.array(),
    updatedAt: z.string(),
    updatedBy: jobProfileUpdatedBySchema.optional(),
});

export type JobProfileComponent = z.infer<typeof jobProfileComponentSchema>;

export const jobProfileLegacySchema = jobProfileComponentSchema.extend({
    actions: z
        .object({
            canAssignReviewer: z.boolean(),
            canEdit: z.boolean(),
        })
        .optional(),
    creator: creatorSchema.optional(),
    description: z.string().optional(),
    profileGenerationInfo: z
        .object({
            inProgress: z.boolean(),
        })
        .optional(),
    reviewer: jobArchitectureProfileReviewerSchema.optional(),
    state: jobProfileStateSchema,
});

export type JobProfileLegacy = z.infer<typeof jobProfileLegacySchema>;

export type JobProfileLegacyWithReviewer = SetRequired<JobProfileLegacy, 'reviewer'>;

export const jobProfileForGenerationWithPromptSchema = jobProfileComponentSchema
    .pick({
        externalId: true,
        skills: true,
        translations: true,
    })
    .transform((jobProfile) => ({
        ...jobProfile,
    }));

export type JobProfileForGenerationWithPrompt = z.infer<typeof jobProfileForGenerationWithPromptSchema>;

export const createJobProfileForGenerationWithPrompt = (
    jobProfile: JobProfileComponent | JobProfileLegacy,
): JobProfileForGenerationWithPrompt => ({
    externalId: jobProfile.externalId,
    skills: jobProfile.skills,
    translations: jobProfile.translations,
});

export const jobProfilesOptionsResponseSchema = z.object({
    readMany: z.object({
        enabled: z.boolean(),
        sortableBy: z.string().array(),
    }),
    skillLimit: z.number(),
    structuredTags: z.string().array(),
});

export type JobProfilesOptionsResponse = z.infer<typeof jobProfilesOptionsResponseSchema>;

export const jobProfilesPermissionsSchema = z.object({
    canUserGetJobProfiles: z.boolean(),
});

export type JobProfilesPermissions = z.infer<typeof jobProfilesPermissionsSchema>;

export const jobProfilesResponseSchema = z.object({
    data: jobProfileLegacySchema.array(),
    pagination: paginationSchema,
});

export type JobProfilesResponse = z.infer<typeof jobProfilesResponseSchema>;

export const jobProfileAssignmentsCountResponseSchema = z.object({
    pagination: z.object({
        count: z.number().nonnegative(),
    }),
});

export type JobProfileAssignmentsCountResponse = z.infer<typeof jobProfileAssignmentsCountResponseSchema>;

const nonEmptyString = z.string().min(1);
const optionalNonEmptyString = nonEmptyString.optional();

export const jobProfileAssignmentsResponseSchema = z.object({
    data: z
        .object({
            id: z.uuid(),
            assignedBy: optionalNonEmptyString,
            firstName: optionalNonEmptyString,
            jobTitle: z.string().optional(),
            lastName: optionalNonEmptyString,
            relocationPreference: relocationPreferenceSchema.optional(),
            skills: skillSchema.array(),
        })
        .array(),
    pagination: paginationSchema,
});

export type JobProfileAssignment = z.infer<typeof jobProfileAssignmentsResponseSchema>['data'][number];

export const userProfileAssignmentsResponseSchema = jobProfileLegacySchema.array();

export type UserProfileAssignmentsResponse = z.infer<typeof userProfileAssignmentsResponseSchema>;

export const userProfileAssignmentsOptionsSchema = z.object({
    canReadUserProfileAssignments: z.boolean(),
});
export type UserProfileAssignmentsOptions = z.infer<typeof userProfileAssignmentsOptionsSchema>;
